import {
  Container,
  Paper,
  Stack,
  Title,
  Text,
  Group,
  Badge,
} from "@mantine/core";
import {
  IconMail,
  IconFingerprint,
  IconBuildingHospital,
  IconLock,
} from "@tabler/icons-react";
import { usePortalAuthStore } from "../../store/portalAuth.store";
import { PhysicianPasskeyManager } from "../../components/portal/PhysicianPasskeyManager";
import { PortalPasswordManager } from "../../components/portal/PortalPasswordManager";

export function PortalAccountPage() {
  const { email, practiceNames } = usePortalAuthStore();

  return (
    <Container size={640} py="xl">
      <>
        <style>{`
          @keyframes fadeSlideUp {
            from { opacity: 0; transform: translateY(12px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .fade-up {
            animation: fadeSlideUp 0.4s ease-out forwards;
          }
          .fade-up-1 {
            animation-delay: 0.05s;
            opacity: 0;
          }
          .fade-up-2 {
            animation-delay: 0.12s;
            opacity: 0;
          }
          .fade-up-3 {
            animation-delay: 0.19s;
            opacity: 0;
          }
        `}</style>

        <Stack gap="xl">
          <Title
            className="fade-up"
            style={{
              fontFamily: "'DM Serif Display', serif",
              fontWeight: 400,
              fontSize: "2rem",
              color: "#1e1e3f",
              letterSpacing: "-0.3px",
            }}
          >
            Account Settings
          </Title>

          <Paper
            withBorder
            p={{ base: "md", sm: "xl" }}
            radius="md"
            className="fade-up fade-up-1"
            style={{
              borderLeft: "3px solid #04119A",
              background: "#ffffff",
            }}
          >
            <Stack gap="md">
              <Group gap={8} align="center">
                <IconMail size={16} color="#04119A" style={{ flexShrink: 0 }} />
                <Text
                  style={{
                    fontFamily: "'IBM Plex Sans', sans-serif",
                    fontWeight: 600,
                    fontSize: 13,
                    color: "#1e1e3f",
                    letterSpacing: "0.04em",
                    textTransform: "uppercase",
                  }}
                >
                  Profile
                </Text>
              </Group>

              <Stack gap={4}>
                <Text
                  size="xs"
                  style={{
                    fontFamily: "'IBM Plex Sans', sans-serif",
                    color: "#9ca3af",
                    fontWeight: 500,
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                  }}
                >
                  Email Address
                </Text>
                <Text
                  size="sm"
                  style={{
                    fontFamily: "'IBM Plex Sans', sans-serif",
                    color: "#1e1e3f",
                    fontWeight: 400,
                  }}
                >
                  {email || "—"}
                </Text>
              </Stack>

              {practiceNames.length > 0 && (
                <Stack gap={8}>
                  <Group gap={6} align="center">
                    <IconBuildingHospital
                      size={14}
                      color="#9ca3af"
                      style={{ flexShrink: 0 }}
                    />
                    <Text
                      size="xs"
                      style={{
                        fontFamily: "'IBM Plex Sans', sans-serif",
                        color: "#9ca3af",
                        fontWeight: 500,
                        letterSpacing: "0.06em",
                        textTransform: "uppercase",
                      }}
                    >
                      Practices
                    </Text>
                  </Group>
                  <Group gap={8} wrap="wrap">
                    {practiceNames.map((name) => (
                      <Badge
                        key={name}
                        variant="light"
                        color="blue"
                        size="sm"
                        style={{
                          fontFamily: "'IBM Plex Sans', sans-serif",
                          fontWeight: 500,
                          background: "rgba(4, 17, 154, 0.06)",
                          color: "#04119A",
                          border: "1px solid rgba(4, 17, 154, 0.12)",
                        }}
                      >
                        {name}
                      </Badge>
                    ))}
                  </Group>
                </Stack>
              )}
            </Stack>
          </Paper>

          <Paper
            withBorder
            p={{ base: "md", sm: "xl" }}
            radius="md"
            className="fade-up fade-up-2"
            style={{
              borderLeft: "3px solid var(--mantine-color-gcpBlue-6)",
              background: "var(--mantine-color-white)",
            }}
          >
            <Stack gap="md">
              <Group gap={8} align="center">
                <IconLock
                  size={16}
                  color="var(--mantine-color-gcpBlue-6)"
                  style={{ flexShrink: 0 }}
                />
                <Text
                  size="sm"
                  fw={600}
                  c="gcpBlue.9"
                  tt="uppercase"
                  lts="0.04em"
                >
                  Password
                </Text>
              </Group>
              <PortalPasswordManager />
            </Stack>
          </Paper>

          <Paper
            withBorder
            p={{ base: "md", sm: "xl" }}
            radius="md"
            className="fade-up fade-up-3"
            style={{
              borderLeft: "3px solid #10b981",
              background: "#ffffff",
            }}
          >
            <Stack gap="md">
              <Group gap={8} align="center">
                <IconFingerprint
                  size={16}
                  color="#10b981"
                  style={{ flexShrink: 0 }}
                />
                <Text
                  style={{
                    fontFamily: "'IBM Plex Sans', sans-serif",
                    fontWeight: 600,
                    fontSize: 13,
                    color: "#1e1e3f",
                    letterSpacing: "0.04em",
                    textTransform: "uppercase",
                  }}
                >
                  Passkeys
                </Text>
              </Group>
              <PhysicianPasskeyManager />
            </Stack>
          </Paper>
        </Stack>
      </>
    </Container>
  );
}

export default PortalAccountPage;
